import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Reveal } from './motion';
import { EASE } from './brand';

const ROLES = [
  {
    id: 'athlete',
    tab: 'Athlete',
    title: 'Show up knowing exactly what you\u2019re doing.',
    bullets: [
      'Tomorrow\u2019s boat, seat and crew \u2014 with air and water temp, wind and river flow on the same card.',
      'Log training in seconds, or let Strava fill it in for you.',
      'Submit test pieces from your phone and watch your % of goal 2k move.',
      'Flag an absence early so nobody builds a lineup around you.',
    ],
    nav: ['Daily Workout', 'Attendance', 'Roster', 'Training Log', 'Equipment', 'My Results', 'My Progress'],
  },
  {
    id: 'coxswain',
    tab: 'Coxswain',
    title: 'Run the boat, and the paperwork that follows it.',
    bullets: [
      'See the full lineup, blades and rigging before you get to the bay.',
      'Enter results for the whole crew, not just yourself.',
      'Mark attendance for your boat, on the water.',
      'Log the row\u2019s meters against the nine people who actually pulled them.',
    ],
    nav: ['Daily Workout', 'Attendance', 'Team results entry', 'Roster', 'Equipment', 'Meters'],
  },
  {
    id: 'coach',
    tab: 'Coach',
    title: 'Build the week once. Everyone sees it instantly.',
    bullets: [
      'Assign boats, oars and seats \u2014 or drop in a saved lineup template.',
      'Scope a session to a squad, cap sign-ups, add walk-ons at the dock.',
      'Publish structured test pieces and get scored results back automatically.',
      'See who\u2019s improving, who\u2019s flat, and who keeps missing sessions.',
    ],
    nav: ['Workout Builder', 'Lineups', 'Announce', 'Training Plan', 'Template Builder', 'Internal Reports', 'Team Analytics'],
  },
  {
    id: 'admin',
    tab: 'Club admin',
    title: 'One club, however many teams it takes.',
    bullets: [
      'Create teams and squads, invite anyone, set their role.',
      'Set your club\u2019s Gold Medal Standard times per boat class.',
      'Point the app at your weather location and USGS river gauge.',
      'Brand it in your colors \u2014 right down to the favicon.',
    ],
    nav: ['Club Management', 'Club Settings', 'Team Management', 'Invitations', 'GMS standards', 'Feature flags'],
  },
];

export default function RoleShowcase() {
  const [active, setActive] = useState('athlete');
  const reduce = useReducedMotion();
  const role = ROLES.find((r) => r.id === active);

  return (
    <section id="roles" className="relative overflow-hidden bg-slate-950 py-20 sm:py-28">
      <div
        className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"
        aria-hidden="true"
      />
      <div className="mx-auto max-w-6xl safe-area-x sm:px-6 lg:px-8">
        <Reveal className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-sky-400">
            Built for every seat
          </p>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-white sm:text-4xl">
            The app rearranges itself around who you are.
          </h2>
          <p className="mt-4 text-lg text-slate-400">
            Roles aren&rsquo;t a permissions checkbox bolted on at the end. Your navigation, your
            data and your reports are assembled from your role and the teams you belong to.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-10">
          <div
            className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0"
            role="tablist"
            aria-label="Roles"
          >
            {ROLES.map((r) => (
              <button
                key={r.id}
                role="tab"
                aria-selected={active === r.id}
                onClick={() => setActive(r.id)}
                className={`relative shrink-0 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                  active === r.id ? 'text-slate-900' : 'text-slate-300 hover:text-white'
                }`}
              >
                {active === r.id && (
                  <motion.span
                    layoutId="role-pill"
                    className="absolute inset-0 rounded-full bg-white"
                    transition={{ duration: 0.35, ease: EASE }}
                  />
                )}
                <span className="relative">{r.tab}</span>
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-8 rounded-3xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur sm:p-10">
          <AnimatePresence mode="wait">
            <motion.div
              key={role.id}
              initial={reduce ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduce ? undefined : { opacity: 0, y: -8 }}
              transition={{ duration: 0.35, ease: EASE }}
              className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr]"
            >
              <div>
                <h3 className="text-2xl font-bold text-white sm:text-3xl">{role.title}</h3>
                <ul className="mt-6 space-y-4">
                  {role.bullets.map((b, i) => (
                    <motion.li
                      key={b}
                      initial={reduce ? false : { opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.35, delay: 0.06 * i, ease: EASE }}
                      className="flex gap-3 text-slate-300"
                    >
                      <svg
                        className="mt-1 h-4 w-4 shrink-0 text-sky-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="leading-relaxed">{b}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>

              {/* the navigation this role actually gets */}
              <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-4">
                <p className="px-1 pb-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  What this role sees
                </p>
                <div className="space-y-1">
                  {role.nav.map((item, i) => (
                    <motion.div
                      key={item}
                      initial={reduce ? false : { opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.05 * i, ease: EASE }}
                      className={`flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm ${
                        i === 0
                          ? 'bg-blue-600/20 font-semibold text-white ring-1 ring-inset ring-blue-500/30'
                          : 'text-slate-400'
                      }`}
                    >
                      <span
                        className={`h-1.5 w-1.5 rounded-full ${
                          i === 0 ? 'bg-sky-400' : 'bg-slate-600'
                        }`}
                      />
                      {item}
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
